import { Link, usePage } from "@inertiajs/react"
import { ChevronDown } from "lucide-react"
import type { ReactNode } from "react"

import { Toaster } from "@/components/ui/sonner"
import { useFlash } from "@/hooks/use-flash"
import logo from "@/assets/marketing/company-logo.png"
import {
  FEATURE_LINKS,
  FOOTER_COLUMNS,
  SOLUTION_LINKS,
  SUPPORT_EMAIL,
  type FooterLink,
} from "@/components/marketing/site-map"
import { new_user_session_path, pricing_path, root_path, start_path } from "@/routes"

/**
 * The marketing shell.
 *
 * Applied by page resolution in `entrypoints/inertia.ts` — every page under
 * `pages/marketing/` gets it without saying so. Before that, each page assigned
 * it by hand at the bottom of the file, and a page that forgot fell through to
 * `PersistentLayout`: every anonymous visitor to the landing page mounting the
 * AI assistant provider and panel, the ⌘K command palette and the shortcuts
 * dialog. None of it is reachable without an account, and all of it is paid for
 * in bundle and in listeners on the one page whose job is to load fast.
 *
 * `storefront` is the density mode from DESIGN.md: same tokens, same fonts,
 * 10px radius and the editorial type scale. The marketing site does not get a
 * design system of its own.
 */
export default function MarketingLayout({ children }: { children: ReactNode }) {
  useFlash()

  return (
    <div className="storefront bg-background text-foreground flex min-h-screen flex-col">
      <MarketingHeader />
      <main className="flex-1">{children}</main>
      <MarketingFooter />
      <Toaster position="bottom-right" />
    </div>
  )
}

function MarketingHeader() {
  const { props } = usePage()
  // Desk profiles (INCO-330) live on this layout and are a page signed-in
  // people reach constantly — every trust chip in the product points at one.
  // Offering them "Log in" is telling someone to do a thing they have already
  // done. The destination is resolved server-side, because which desk is
  // "theirs" is a question only the server can answer.
  const deskUrl = props.viewer_desk_url as string | null | undefined

  return (
    <header className="border-b">
      <nav className="mx-auto flex h-16 max-w-measure items-center justify-between px-5 lg:px-8">
        <Link href={root_path()} className="flex items-center" aria-label="Inco home">
          {/*
            The wordmark is dark ink on transparent, which disappeared entirely
            in Desk mode. The marketing pages are effectively light-only, but a
            desk profile (INCO-330) is on this layout and is reached constantly
            by signed-in people, who may well be in Desk mode.
          */}
          <img src={logo} alt="Inco" className="h-7 w-auto dark:invert" />
        </Link>

        <div className="flex items-center gap-1">
          {/*
            Product and Solutions are `<details>` rather than a JS dropdown, and
            that is the point: the links are in the HTML on first paint, so a
            crawler and an assistant reading the raw page both find them. A
            menu that only exists after hydration is a menu the readers who
            matter most never open. Hidden below `md` — the footer carries the
            same inventory, and a phone does not want two of them.
          */}
          <NavMenu label="Product" links={FEATURE_LINKS} />
          <NavMenu label="Solutions" links={SOLUTION_LINKS} />
          <Link
            href={pricing_path()}
            className="text-muted-foreground hover:text-foreground rounded-lg px-3 py-2 text-sm transition-colors"
          >
            Pricing
          </Link>
          {deskUrl ? (
            <a
              href={deskUrl}
              className="border-input hover:bg-accent rounded-lg border px-3 py-2 text-sm font-medium transition-colors"
            >
              Your desk
            </a>
          ) : (
            <>
              <Link
                href={new_user_session_path()}
                className="text-muted-foreground hover:text-foreground rounded-lg px-3 py-2 text-sm transition-colors"
              >
                Log in
              </Link>
              {/*
                The one oxblood action per view, and on the marketing pages it
                is always this one. Section CTAs inside a page use the shared
                StartDeskCta, which renders the same destination.
              */}
              <Link
                href={start_path()}
                className="bg-primary text-primary-foreground hover:bg-primary/90 ml-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
              >
                Start your desk
              </Link>
            </>
          )}
        </div>
      </nav>
    </header>
  )
}

/**
 * A header dropdown that works without JavaScript.
 *
 * `<details>` opens on click natively, so the links are in the document from
 * the first paint rather than after hydration — which matters here because
 * INCO-400 invites GPTBot, ClaudeBot and PerplexityBot to this site, and none
 * of them run JavaScript. A crawler that cannot see the menu cannot follow it.
 */
function NavMenu({ label, links }: { label: string; links: FooterLink[] }) {
  return (
    <details className="group relative hidden md:block">
      <summary className="text-muted-foreground hover:text-foreground marker:content-none flex cursor-pointer list-none items-center gap-1 rounded-lg px-3 py-2 text-sm transition-colors">
        {label}
        <ChevronDown className="size-3.5 transition-transform group-open:rotate-180" aria-hidden />
      </summary>
      <ul className="bg-card absolute left-0 z-20 mt-1 w-60 rounded-lg border p-1.5 shadow-sm">
        {links.map((link) => (
          <li key={link.href}>
            <Link
              href={link.href}
              className="text-muted-foreground hover:bg-accent hover:text-foreground block rounded px-2.5 py-1.5 text-sm transition-colors"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </details>
  )
}

/**
 * The footer is the site's crawl graph as much as it is navigation.
 *
 * A visitor landing on /pricing from a search result previously had one link
 * out of it, and a crawler had no path to any page not linked from the
 * homepage. `FOOTER_COLUMNS` is the full public inventory, and
 * `spec/design/marketing_site_spec.rb` fails when a marketing page exists that
 * no column links — so a page added in INCO-396 or INCO-397 cannot ship
 * orphaned.
 */
function MarketingFooter() {
  return (
    <footer className="border-t">
      <div className="mx-auto max-w-measure px-5 py-12 lg:px-8">
        <div className="flex flex-col gap-10 sm:flex-row sm:justify-between">
          <div>
            <img src={logo} alt="Inco" className="h-6 w-auto dark:invert" />
            <p className="text-muted-foreground mt-3 max-w-xs text-sm leading-relaxed">
              One system for a commodity desk, from the first forwarded offer to
              the final contract.
            </p>
          </div>

          <nav
            aria-label="Site"
            className="grid grid-cols-2 gap-x-10 gap-y-8 sm:grid-cols-4 sm:gap-x-12"
          >
            {FOOTER_COLUMNS.map((column) => (
              <div key={column.heading}>
                <h2 className="text-muted-foreground text-xs font-semibold">
                  {column.heading}
                </h2>
                <ul className="mt-3 flex flex-col gap-2">
                  {column.links.map((link) => (
                    <li key={link.label}>
                      {/*
                        `external` covers the mailto and anything off-app. Inertia's
                        Link would try to fetch those as a page visit.
                      */}
                      {link.external ? (
                        <a
                          href={link.href}
                          className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                        >
                          {link.label}
                        </a>
                      ) : (
                        <Link
                          href={link.href}
                          className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                        >
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        <div className="mt-10 flex flex-col gap-2 border-t pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-muted-foreground text-xs">© {new Date().getFullYear()} Inco</p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-muted-foreground hover:text-foreground text-xs transition-colors"
          >
            {SUPPORT_EMAIL}
          </a>
        </div>
      </div>
    </footer>
  )
}
